import React from 'react';
import { Switch, Route } from 'react-router';
import { RouteBoundary } from 'signer-app/parts/error-boundary';
import { LabeledRoute } from 'hellospa/components/labeled-route';
import { useFeatureFlag } from '../js/sign-components/common/feature-flag';

const DropboxConnectPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxconnect" */ 'hellospa/page/dropbox/connect'
    ),
);
const DropboxErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxerrorpage" */ 'hellospa/components/deep-integration/error-page'
    ),
);
const DropboxSsoPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxssopage" */ 'hellospa/components/deep-integration/sso-page'
    ),
);
const DropboxConfirmStorageAccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-confirmstorageaccess" */ 'hellospa/components/deep-integration/storage-access/confirm'
    ),
);
const DropboxRequestStorageAccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-requeststorageaccess" */ 'hellospa/components/deep-integration/storage-access/request'
    ),
);
const RecoverPassword = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-recoverPassword" */ 'hellospa/page/recover-password-page'
    ),
);
const NothingToSignPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-nothingToSign" */ 'hellospa/page/nothing-to-sign-page'
    ),
);
const IntegrationStatusPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-integrationstatus" */ 'hellospa/components/integration/workflow-status'
    ),
);
const IntegrationCreateOneTimeDoc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-integrationcreateonetimedoc" */ 'hellospa/components/integration/create-one-time-doc'
    ),
);
const IntegrationCreateTemplate = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-integrationcreatetemplate" */ 'hellospa/components/integration/create-template'
    ),
);
const IntegrationSendTemplatePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationSendTemplate" */ 'hellospa/components/integration/send-template'
    ),
);
const IntegrationEditTemplatePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationEditTemplate" */ 'hellospa/components/integration/edit-template'
    ),
);
const IntegrationSendRemindersPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationSendReminders" */ 'hellospa/components/integration/send-reminders'
    ),
);
const IntegrationCancelSignatureRequestPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-IntegrationCancelSignatureRequest" */ 'hellospa/components/integration/cancel-signature-request'
    ),
);
const DropboxLoginVerificationPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-dropboxloginverification" */ 'hellospa/page/dropbox/login-verification'
    ),
);
const OfficeWordSelfSign = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-officewordselfsign" */ 'hellospa/components/microsoft/selfsign'
    ),
);
const OfficeWordSendSignature = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-officewordsendsignature" */ 'hellospa/components/microsoft/sendsignature'
    ),
);
const OfficeWordSettings = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-officewordsettings" */ 'hellospa/components/microsoft/settings'
    ),
);
const ThrowAFrontendErrorPleasePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-throwAFrontendErrorPlease" */ 'hellospa/page/throw-a-frontend-error-please'
    ),
);
const SharePointSetup = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-sharepoint" */ 'hellospa/components/sharepoint'
    ),
);
const SharePointStatusPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-sharepointstatus" */ 'hellospa/components/sharepoint/status-page'
    ),
);
const SharePointSendForSignature = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-sharepoint-send-for-signature" */ 'hellospa/components/sharepoint/send-for-signature'
    ),
);
const SignUpPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-signuppage" */ 'hellospa/page/authentication/sign-up-page'
    ),
);
const SignInPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-signinpage" */ 'hellospa/page/authentication/sign-in-page'
    ),
);
const SignInMobileApp = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-signinmobileapp" */ 'hellospa/page/authentication/sign-in-mobile-app'
    ),
);
const JoinTeamPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-jointeampage" */ 'hellospa/page/authentication/join-team-page'
    ),
);
const NoAccessErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-noaccesserrorpage" */ 'hellospa/page/no-access-error-page'
    ),
);
const AcceptTermsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-acceptterms" */ 'hellospa/page/authentication/accept-terms-page'
    ),
);

const BrandingDemoPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellospa-brandingdemo" */ 'hellospa/page/branding-demo/branding-demo-page'
    ),
);

const WhiteLabelingSignerDemoOld = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellosign-whitelabeling" */ 'hellosign/components/api/white-labeling/white-labeling-signer-demo'
    ),
);

const WhiteLabelingSignerDemoNew = React.lazy(
  () =>
    import(
      /* webpackChunkName: "hellosign-whitelabelingapp" */ 'hellosign/components/api/white-labeling/white-labeling-signer-app-demo'
    ),
);

// This component is different from the ones above because it's imported directly from hellosign.
// It's used to render signer app in iframes for white-labeling demos (landing page and docs)
const WhiteLabelingSignerAppDemo = () => {
  const ff = useFeatureFlag('sign_core_2025_12_08_white_labeling_swap');

  return ff ? <WhiteLabelingSignerDemoNew /> : <WhiteLabelingSignerDemoOld />;
};

const NotFound = () => {
  // This will trigger our ErrorBoundary and report to Sentry The URL will be
  // part of the report.
  throw new Error('404 not found');
};

// These routes will be accessible by all visitors,
// regardless of whether or not they're logged in.

// <Switch doesn't like react fragments, so we have
// to use an array with keys.
//
// These keys only need to be locally unique.
const commonRoutes = [
  <Route
    key="FrontendError"
    exact
    path="/info/throwAFrontendErrorPlease"
    component={ThrowAFrontendErrorPleasePage}
  />,
  <LabeledRoute
    key="NoAccess"
    label="NoAccess"
    path="/info/noAccess"
    component={NoAccessErrorPage}
  />,
  <LabeledRoute
    key="JoinTeam"
    label="Join team"
    path="/account/confirmTeamInvite"
    component={JoinTeamPage}
  />,
];

/**
 * Routes are defined outside of a component so that you don't get an
 * opportunity to pass custom data into your component. That became a mess in
 * HelloSPA, so going forward you will need to fetch the data you need from the
 * component.
 */
function LoggedOutRoutes() {
  const removeSharepoint = useFeatureFlag(
    'sign_integrations_2026_01_08_remove_sharepoint',
  );

  const sharepointRoutes = !removeSharepoint
    ? [
        <Route
          key="sharepoint-setup"
          path="/sharepoint/setup"
          component={SharePointSetup}
        />,
        <Route
          key="sharepoint-status"
          path="/sharepoint/status"
          component={SharePointStatusPage}
        />,
        <Route
          key="sharepoint-send"
          path="/sharepoint/sendForSignature"
          component={SharePointSendForSignature}
        />,
      ]
    : [];

  return (
    <Switch>
      {commonRoutes}
      <LabeledRoute
        label="Create an account"
        path="/account/signUp"
        component={SignUpPage}
      />
      <LabeledRoute
        label="Sign in"
        path="/account/logIn"
        component={SignInPage}
      />
      <LabeledRoute
        label="Sign in"
        path="/account/mobileLogIn"
        component={SignInMobileApp}
      />
      <LabeledRoute
        label="Accept terms"
        path="/account/acceptTerms"
        component={AcceptTermsPage}
      />
      <Route path="/dropbox/connect" component={DropboxConnectPage} />
      <Route
        path="/dropbox/logInVerification"
        component={DropboxLoginVerificationPage}
      />
      <RouteBoundary
        path="/dropbox/confirmStorageAccess"
        name="confirmStorage"
        component={DropboxConfirmStorageAccess}
      />
      <RouteBoundary
        path="/dropbox/error"
        name="dropboxError"
        component={DropboxErrorPage}
      />
      <Route
        path="/dropbox/requestStorageAccess"
        component={DropboxRequestStorageAccess}
      />
      <Route path="/dropbox/sso" component={DropboxSsoPage} />
      <Route
        path="/integration/upgradeRequired"
        component={IntegrationStatusPage}
      />
      <Route
        path="/integration/enableThirdPartyCookies"
        component={IntegrationStatusPage}
      />
      <Route path="/integration/status" component={IntegrationStatusPage} />
      <Route
        path="/integration/createOneTimeDoc"
        component={IntegrationCreateOneTimeDoc}
      />
      <Route
        path="/integration/createTemplate"
        component={IntegrationCreateTemplate}
      />
      <Route
        path="/integration/sendTemplate"
        component={IntegrationSendTemplatePage}
      />
      <Route
        path="/integration/editTemplate"
        component={IntegrationEditTemplatePage}
      />
      <Route
        path="/integration/sendReminders"
        component={IntegrationSendRemindersPage}
      />
      <Route
        path="/integration/cancelSignatureRequest"
        component={IntegrationCancelSignatureRequestPage}
      />
      <Route path="/microsoft/selfSign" component={OfficeWordSelfSign} />
      <Route
        path="/microsoft/sendSignature"
        component={OfficeWordSendSignature}
      />
      <Route path="/microsoft/settings" component={OfficeWordSettings} />
      {sharepointRoutes}
      <LabeledRoute
        label="RecoverPassword"
        path="/account/recoverPassword"
        component={RecoverPassword}
      />
      <LabeledRoute
        label="NothingToSign"
        path="/info/nothingToSign"
        component={NothingToSignPage}
      />
      <Route path="/api/embedded-branding-demo" component={BrandingDemoPage} />
      <Route
        path="/api/white-labeling-signer-demo"
        component={WhiteLabelingSignerAppDemo}
      />
      <Route component={NotFound} />
    </Switch>
  );
}

export const loggedOutRoutes = <LoggedOutRoutes />;
